import { useState, type MouseEvent } from 'react';
import { usePermissions, useRecordContext } from 'react-admin';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { useFeature } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { sessionCompanyId, sessionUserId } from '../../authProvider';
import { isImpersonationTab } from '../../session/sessionToken';
import {
  BUTTON_LABEL,
  DIALOG_NOTE,
  FEATURE_IMPERSONATION,
  IMPERSONATION_REASON_MAX,
  POPUP_BLOCKED,
  START_ERROR,
  apiMessage,
  canImpersonateTarget,
  impersonationUrl,
  reasonProblem,
  startImpersonation,
} from './impersonationRules';

/**
 * „Zaloguj jako” przy koncie (decyzja Bartka 02.11.2026, przełącznik
 * `impersonation`, S-02.11-01) — na liście użytkowników (ikona) i na karcie
 * konta (przycisk). Widoczny tylko z uprawnieniem `IMPERSONATE`, przy koncie,
 * które wolno przejąć, i nie w karcie, która sama jest przejęciem.
 *
 * Okno pyta o powód (co najmniej 10 znaków), a „Zaloguj jako” otwiera nową
 * kartę **od razu przy kliknięciu** (inaczej przeglądarka uzna ją za
 * wyskakujące okno) i dopiero potem prosi API o kod; nowa karta wymienia go
 * na sesję (`ImpersonatePage`).
 */
export const ImpersonateButton = ({ variant = 'list' }: { variant?: 'list' | 'toolbar' }) => {
  const record = useRecordContext();
  const { permissions } = usePermissions<string>();
  const featureOn = useFeature(FEATURE_IMPERSONATION);
  const allowed = useHasPermission('IMPERSONATE');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!record || !featureOn || !allowed || isImpersonationTab()) return null;
  if (
    !canImpersonateTarget(
      { id: sessionUserId(), role: permissions, companyId: sessionCompanyId() },
      record,
    )
  ) {
    return null;
  }

  const name = [record.firstName, record.lastName]
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map((p) => p.trim())
    .join(' ');
  const who = name || String(record.username ?? `konto nr ${String(record.id)}`);

  const openDialog = (e: MouseEvent) => {
    // Na liście wiersz otwiera edycję — przycisk nie może tego zrobić.
    e.stopPropagation();
    setError(null);
    setOpen(true);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    setReason('');
    setError(null);
  };

  const confirm = async () => {
    const problem = reasonProblem(reason);
    if (problem) {
      setError(problem);
      return;
    }
    const tab = window.open('', '_blank');
    if (!tab) {
      setError(POPUP_BLOCKED);
      return;
    }
    try {
      // Nowa karta nie ma dostępu do tej — nic do niej nie mówi.
      tab.opener = null;
    } catch {
      // Starsze przeglądarki — bez znaczenia dla przejęcia.
    }
    setBusy(true);
    try {
      const code = await startImpersonation(Number(record.id), reason);
      tab.location.href = impersonationUrl(window.location.origin, code);
      setOpen(false);
      setReason('');
      setError(null);
    } catch (e) {
      tab.close();
      setError(apiMessage(e, START_ERROR));
    } finally {
      setBusy(false);
    }
  };

  const trigger =
    variant === 'toolbar' ? (
      <Button
        size="small"
        startIcon={<LoginIcon />}
        onClick={openDialog}
        data-testid="impersonate-button"
      >
        {BUTTON_LABEL}
      </Button>
    ) : (
      <Tooltip title={`${BUTTON_LABEL}: ${who}`}>
        <IconButton
          size="small"
          aria-label={BUTTON_LABEL}
          onClick={openDialog}
          data-testid={`impersonate-button-${String(record.id)}`}
        >
          <LoginIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );

  return (
    <>
      {trigger}
      <Dialog
        open={open}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
        data-testid="impersonate-dialog"
      >
        <DialogTitle>{`${BUTTON_LABEL}: ${who}`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {DIALOG_NOTE}
          </Typography>
          <TextField
            label="Powód"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            helperText="Co najmniej 10 znaków — trafi do dziennika działań."
            slotProps={{ htmlInput: { maxLength: IMPERSONATION_REASON_MAX, 'data-testid': 'impersonate-reason' } }}
            autoFocus
          />
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="impersonate-error">
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirm()}
            disabled={busy}
            data-testid="impersonate-confirm"
          >
            {BUTTON_LABEL}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
